import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import "./Styles.css";
import { format } from "date-fns";
import TableModal from "../../../../components/reusableComponent/TableModal";
import { useGetEmailFirstResponseClickToExpandQuery } from "../../../../Services/dashboard/dashboard";

const toYYYYMMDD = (d: string): string => {
  if (!d) return "";
  const s = typeof d === "string" && d.length >= 10 ? d.slice(0, 10) : d;
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : format(new Date(d), "yyyy-MM-dd");
};

const formatDateDDMMYYYY = (yyyyMmDd: string): string => {
  if (!yyyyMmDd || yyyyMmDd.length < 10) return yyyyMmDd;
  const [y, m, d] = yyyyMmDd.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : yyyyMmDd;
};

interface Iprops {
  emailDashboardData: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
}

const EMAIL_TABLE_COLUMNS = [
  { label: "Subject", render: (row: any) => row?.subject ?? "—" },
  { label: "Contact Name", render: (row: any) => row?.contact_name ?? "—" },
  { label: "Contact Email", render: (row: any) => row?.contact_email ?? "—" },
  { label: "Contact Phone", render: (row: any) => row?.contact_phone ?? "—" },
  {
    label: "Created",
    render: (row: any) => {
      const raw = row?.created;
      if (!raw) return "—";
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return "—";
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
    },
  },
];

const SentChart: React.FC<Iprops> = ({ emailDashboardData, startDate, endDate, teamId }) => {
  const navigate = useNavigate();
  const hoveredPointRef = useRef<any>(null);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);

  const dailyStats = emailDashboardData?.sent_emails_per_day?.daily_data || [];
  const chartData = dailyStats.map((stat) => {
    const rawDate = toYYYYMMDD(stat?.date ?? "");
    return {
      date: format(new Date(stat?.date), "dd MMM"),
      rawDate,
      sent: stat?.count ?? 0,
    };
  });

  const clickToExpandParams =
    tableModalOpen && selectedDate && teamId && startDate && endDate
      ? {
          graph_name: "sent_emails_per_day",
          value: selectedDate,
          start_date: startDate,
          end_date: endDate,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetEmailFirstResponseClickToExpandQuery(
      clickToExpandParams ?? { graph_name: "", value: "", start_date: "", end_date: "", team_id: "" },
      { skip: !clickToExpandParams }
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw = (clickToExpandData as any)?.results ?? (clickToExpandData as any)?.data ?? clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 10;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedDate]);

  const handlePointClick = (data: any) => {
    if (!teamId || !startDate || !endDate || !data?.rawDate) return;
    setSelectedDate(data.rawDate);
    setTableModalOpen(true);
    setClickToExpandPage(1);
  };

  const renderClickableDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    const canClick = !!(teamId && startDate && endDate && payload?.rawDate);
    return (
      <circle
        cx={cx}
        cy={cy}
        r={14}
        fill="transparent"
        style={{ cursor: canClick ? "pointer" : "default" }}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          if (canClick) handlePointClick(payload);
        }}
      />
    );
  };

  const ResponsiveChartContainer = ResponsiveContainer as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;
  const TooltipContainer = Tooltip as any;
  const CartesianGridContainer = CartesianGrid as any;
  const AreaChartContainer = AreaChart as any;
  const AreaContainer = Area as any;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      hoveredPointRef.current = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.96)",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "12px 16px",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            cursor: teamId && startDate && endDate ? "pointer" : "default",
          }}
        >
          <p
            style={{
              margin: 0,
              fontWeight: 600,
              fontSize: "13px",
              color: "#374151",
              marginBottom: "4px",
            }}
          >
            {label}
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#c251ed",
              fontWeight: 700,
            }}
          >
            {payload[0].value} emails
          </p>
          {teamId && startDate && endDate && (
            <p style={{ margin: "4px 0 0 0", fontSize: "11px", color: "#94a3b8" }}>Click to view details</p>
          )}
        </div>
      );
    }
    hoveredPointRef.current = null;
    return null;
  };

  const handleChartClick = () => {
    const data = hoveredPointRef.current;
    if (data?.rawDate && teamId && startDate && endDate) handlePointClick(data);
  };

  return (
    <div className="card card-flush h-md-90 mb-5 mb-xl-10">
      <div className="card-header pt-5 pb-3">
        <div className="card-title d-flex flex-column">
          <div className="d-flex align-items-center">
            <span className="fs-2hx fw-bold text-dark me-2 lh-1 ls-n2">
              {emailDashboardData?.sent_emails_per_day?.current_outbox}
            </span>
            {/* <span className="text-gray-400 pt-1 fw-semibold mx-4">
              {emailDashboardData?.sent_emails_per_day?.outbox_percentage_change > 0 ? (
                <>
                  <i className="bi bi-arrow-up text-success fs-2"></i>
                  <span className="text-success">
                    {Number.isInteger(
                      emailDashboardData.sent_emails_per_day.outbox_percentage_change
                    )
                      ? emailDashboardData.sent_emails_per_day.outbox_percentage_change
                      : emailDashboardData.sent_emails_per_day.outbox_percentage_change?.toFixed(
                          2
                        )}
                    %
                  </span>
                </>
              ) : emailDashboardData?.sent_emails_per_day?.outbox_percentage_change <
                0 ? (
                <>
                  <i className="bi bi-arrow-down text-danger fs-2"></i>
                  <span className="text-danger">
                    {Number.isInteger(
                      Math.abs(
                        emailDashboardData.sent_emails_per_day.outbox_percentage_change
                      )
                    )
                      ? Math.abs(
                          emailDashboardData.sent_emails_per_day
                            .outbox_percentage_change
                        )
                      : Math.abs(
                          emailDashboardData.sent_emails_per_day
                            .outbox_percentage_change
                        )?.toFixed(2)}
                    %
                  </span>
                </>
              ) : (
                <span>-</span>
              )}
            </span> */}
          </div>
          <span className="text-gray-600 pt-1 fw-semibold fs-6">
            Sent messages
          </span>
        </div>
      </div>
      <div
        className="card-body pt-2 pb-4 px-0"
        style={{
          cursor: teamId && startDate && endDate ? "pointer" : "default",
          position: "relative",
        }}
        onClick={handleChartClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleChartClick(); }}
      >
        <div
          className={teamId && startDate && endDate ? "email-dashboard-chart-clickable chart-pointer" : ""}
          style={{ cursor: teamId && startDate && endDate ? "pointer" : "default", width: "100%", height: 240 }}
        >
        <ResponsiveChartContainer width="100%" height={240}>
          <AreaChartContainer
            data={chartData}
            margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
          >
            <defs>
              <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#c251ed" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#e8b4f8" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGridContainer
              strokeDasharray="3 3"
              stroke="#f0f0f0"
              vertical={false}
            />
            <XAxisContainer
              dataKey="date"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxisContainer
              allowDecimals={false}
              domain={[0, "auto"]}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              tickFormatter={(value) => Math.floor(value).toString()}
            />
            <TooltipContainer
              content={<CustomTooltip />}
              cursor={{
                stroke: "#c251ed",
                strokeWidth: 1,
                strokeDasharray: "5 5",
              }}
            />
            <AreaContainer
              type="monotone"
              dataKey="sent"
              stroke="#c251ed"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorSent)"
              animationDuration={800}
              dot={renderClickableDot}
              activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff", cursor: teamId && startDate && endDate ? "pointer" : "default" }}
            />
          </AreaChartContainer>
        </ResponsiveChartContainer>
        </div>
      </div>

      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={() => {
          setTableModalOpen(false);
          setSelectedDate(null);
          setClickToExpandPage(1);
        }}
        title={selectedDate ? `Sent – ${formatDateDDMMYYYY(selectedDate)}` : "Leads"}
        contentLabel="Sent click-to-expand"
        emptyMessage="No emails in this segment."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx) => row?.thread_id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const threadId = row?.thread_id;
          if (threadId != null) {
            setTableModalOpen(false);
            setSelectedDate(null);
            navigate(`/email/inbox/${threadId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={EMAIL_TABLE_COLUMNS}
      />
    </div>
  );
};

export default SentChart;
